import { useEffect, useMemo, useState } from 'react';
import { geoEqualEarth, geoPath } from 'd3-geo';
import { scaleLinear } from 'd3-scale';
import { chartColors, mapStyle } from '../../../design/charts.js';
import { useTheme } from '../components/theme-provider';
import { t } from '../i18n';
import { fmtValue } from '../lib/format';

export interface CountryData {
  country: string;
  iso3c?: string;
  region?: string;
  median: number;
  n: number;
  min: number;
  max: number;
  utilities: Array<{
    id: number;
    name: string;
    ownership: string | null;
    type: string | null;
    value: number;
    rank: number;
  }>;
}

import { DEFAULT_COMPARE_COLORS, getCompareColor, MAP_HEATMAP, PALETTE } from './palette';
export { DEFAULT_COMPARE_COLORS, getCompareColor };

interface Props {
  countriesData: Map<string, CountryData>;
  units: string | null;
  inverted?: boolean;
  selectedCountry?: string | null;
  onSelectCountry?: (country: string | null) => void;
  multiSelect?: boolean;
  selectedCountries?: string[];
  onToggleCountry?: (country: string) => void;
  countryColors?: Record<string, string>;
}

// Normalise common name differences between DB and TopoJSON / GeoJSON
export const GEO_NAME_ALIASES: Record<string, string> = {
  'Central African Rep.': 'Central African Republic',
  'Dem. Rep. Congo': 'Congo, Dem. Rep.',
  'Congo': 'Congo, Rep.',
  "Côte d'Ivoire": 'Côte d’Ivoire',
  'Egypt': 'Egypt, Arab Rep.',
  'Eq. Guinea': 'Equatorial Guinea',
  'eSwatini': 'Eswatini',
  'Gambia': 'Gambia, The',
  'S. Sudan': 'South Sudan',
};

export const DB_TO_GEO_NAME: Record<string, string> = {
  'Central African Republic': 'Central African Rep.',
  'Congo, Dem. Rep.': 'Dem. Rep. Congo',
  'Congo, Rep.': 'Congo',
  'Côte d’Ivoire': "Côte d'Ivoire",
  'Egypt, Arab Rep.': 'Egypt',
  'Equatorial Guinea': 'Eq. Guinea',
  'Eswatini': 'eSwatini',
  'Gambia, The': 'Gambia',
  'South Sudan': 'S. Sudan',
};

export { AFRICAN_COUNTRIES, isAfricanCountry, AFRICAN_COUNTRY_LIST } from '@upbeat/shared';

export function AfricaMap({
  countriesData,
  units,
  inverted = false,
  selectedCountry = null,
  onSelectCountry,
  multiSelect = false,
  selectedCountries = [],
  onToggleCountry,
  countryColors,
}: Props) {
  const [geoData, setGeoData] = useState<any | null>(null);
  const [hoveredGeoName, setHoveredGeoName] = useState<string | null>(null);

  // Load self-hosted GeoJSON
  useEffect(() => {
    let active = true;
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    fetch(`${base}/data/africa.json`)
      .then((r) => r.json())
      .then((data) => {
        if (active) setGeoData(data);
      })
      .catch((err) => console.error('Failed to load africa.json:', err));
    return () => {
      active = false;
    };
  }, []);

  const width = 400;
  const height = 540;

  // D3 projection calibrated closely around Africa bounds to minimize blank margins
  const { pathGenerator, features } = useMemo(() => {
    if (!geoData) return { pathGenerator: null, features: [] };

    const proj = geoEqualEarth().fitExtent(
      [
        [12, 12],
        [width - 12, height - 12],
      ],
      geoData
    );
    const pathGen = geoPath(proj);
    const feats = geoData.features || [];

    return { pathGenerator: pathGen, features: feats };
  }, [geoData]);

  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === 'light';

  // Color scale for countries with data:
  // Standard ColorBrewer RdYlGn 5-stop diverging scale (Red = Worse, Sand = Neutral, Green = Better)
  const colorScale = useMemo(() => {
    const medians: number[] = [];
    countriesData.forEach((c) => {
      if (Number.isFinite(c.median)) medians.push(c.median);
    });

    if (medians.length === 0) return () => mapStyle.base;

    medians.sort((a, b) => a - b);
    const minVal = medians[0]!;
    const maxVal = medians[medians.length - 1]!;

    if (minVal === maxVal) return () => MAP_HEATMAP.good;

    // Standard ColorBrewer 5-stop ramp:
    // inverted = false (higher is better): minVal -> bad (red), maxVal -> good (green)
    // inverted = true (lower is better): minVal -> good (green), maxVal -> bad (red)
    const ramp = inverted
      ? [
          MAP_HEATMAP.good,
          MAP_HEATMAP.goodMid,
          MAP_HEATMAP.neutral,
          MAP_HEATMAP.badMid,
          MAP_HEATMAP.bad,
        ]
      : [
          MAP_HEATMAP.bad,
          MAP_HEATMAP.badMid,
          MAP_HEATMAP.neutral,
          MAP_HEATMAP.goodMid,
          MAP_HEATMAP.good,
        ];

    const step = (maxVal - minVal) / 4;
    return scaleLinear<string>()
      .domain([
        minVal,
        minVal + step,
        minVal + step * 2,
        minVal + step * 3,
        maxVal,
      ])
      .range(ramp);
  }, [countriesData, inverted, isLight]);

  // Helper to look up data given geojson feature name
  const getCountryStats = (geoName: string): CountryData | undefined => {
    const direct = countriesData.get(geoName);
    if (direct) return direct;
    const dbName = GEO_NAME_ALIASES[geoName];
    if (dbName) return countriesData.get(dbName);
    return undefined;
  };

  const activeGeoName = selectedCountry ? DB_TO_GEO_NAME[selectedCountry] || selectedCountry : null;

  const sortedFeatures = useMemo(() => {
    return [...features].sort((a: any, b: any) => {
      const aName = a.properties?.name;
      const bName = b.properties?.name;
      const aDbName = GEO_NAME_ALIASES[aName] || aName;
      const bDbName = GEO_NAME_ALIASES[bName] || bName;

      const aActive = multiSelect
        ? selectedCountries.includes(aDbName) || aName === hoveredGeoName
        : aName === activeGeoName || aName === hoveredGeoName;
      const bActive = multiSelect
        ? selectedCountries.includes(bDbName) || bName === hoveredGeoName
        : bName === activeGeoName || bName === hoveredGeoName;

      if (aActive && !bActive) return 1;
      if (!aActive && bActive) return -1;
      return 0;
    });
  }, [features, activeGeoName, hoveredGeoName, multiSelect, selectedCountries]);

  return (
    <div className="relative flex flex-col items-center justify-center w-full min-h-[460px] select-none">
      {!geoData ? (
        <div className="skeleton w-full max-w-[480px] aspect-[400/540] rounded-lg" />
      ) : (
        <div className="relative w-full max-w-[480px]">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-auto drop-shadow-[0_14px_44px_rgba(240,164,49,0.14)]"
            role="img"
            aria-label={t.mapAria}
          >
            {/* Country Polygons */}
            <g>
              {sortedFeatures.map((f: any) => {
                const geoName = f.properties?.name;
                const dbName = GEO_NAME_ALIASES[geoName] || geoName;
                const stats = getCountryStats(geoName);
                const hasData = !!stats;

                let isSelected = false;
                let countryColor: string = PALETTE.primary;

                if (multiSelect) {
                  isSelected = selectedCountries.includes(dbName);
                  if (isSelected && countryColors?.[dbName]) {
                    countryColor = countryColors[dbName];
                  }
                } else {
                  isSelected = activeGeoName === geoName;
                }

                const isHovered = hasData && hoveredGeoName === geoName;

                let fill = mapStyle.base;
                if (hasData) {
                  fill = colorScale(stats.median);
                }

                return (
                  <path
                    key={geoName}
                    d={pathGenerator ? pathGenerator(f) || undefined : undefined}
                    fill={fill}
                    stroke={
                      isSelected
                        ? countryColor
                        : isHovered
                        ? 'var(--gold)'
                        : 'var(--map-stroke)'
                    }
                    strokeWidth={isSelected ? (isHovered ? 3.5 : 3) : isHovered ? 1.8 : 0.95}
                    strokeLinejoin="round"
                    className={`transition-colors duration-200 ${hasData ? 'cursor-pointer' : 'cursor-default'}`}
                    onMouseEnter={() => {
                      if (hasData) setHoveredGeoName(geoName);
                    }}
                    onMouseLeave={() => {
                      if (hasData) setHoveredGeoName(null);
                    }}
                    onClick={
                      hasData
                        ? () => {
                            if (multiSelect) {
                              onToggleCountry?.(dbName);
                            } else {
                              onSelectCountry?.(selectedCountry === dbName ? null : dbName);
                            }
                          }
                        : undefined
                    }
                  >
                    <title>
                      {geoName}
                      {isSelected && multiSelect ? t.selectedInComparison : ''}
                      {stats
                        ? ` · ${t.kpiMedian}: ${units === 'per unit' ? fmtValue(units, stats.median) : `${stats.median.toFixed(2)}${units ? ' ' + units : ''}`} (${t.nUtilities(String(stats.n))})`
                        : ` · ${t.noDataCard}`}
                    </title>
                  </path>
                );
              })}
            </g>
          </svg>

          {/* Interactive floating indicator / legend bar */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-[11px] px-4 py-2 mt-2 bg-glass-2 border border-hair rounded-md w-full max-w-[440px] mx-auto select-none">
            <span className="text-right text-rose-600 dark:text-rose-400 font-medium leading-tight">
              {t.mapWorse(inverted)}
            </span>
            <div
              className="w-28 sm:w-32 h-2 rounded-full shrink-0 shadow-inner"
              style={{
                background:
                  'linear-gradient(90deg, #D73027, #F46D43, #FEE08B, #A6D96A, #1A9850)',
              }}
            />
            <span className="text-left text-emerald-700 dark:text-emerald-400 font-semibold leading-tight">
              {t.mapBetter(inverted)}
            </span>
          </div>

          {/* Equal Earth projection note */}
          <div className="text-[10px] text-dim text-center mt-2 px-2 leading-relaxed">
            {t.mapProjectionNote}
          </div>
        </div>
      )}
    </div>
  );
}
